// src/components/TxnDetailMethodTab.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

function TxnDetailMethodTab() {
  const { authFetch } = useAuth();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [newAlias, setNewAlias] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMethod, setNewMethod] = useState({ type: 'Transaction', code: '', name: '', provider: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [unmapped, setUnmapped] = useState([]);
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all' | 'Transaction' | 'Disbursement'

  const fetchMethods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/txn-detail-methods');
      const data = await response.json();
      setMethods(data);
    } catch (error) {
      console.error('Gagal mengambil data method:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const fetchUnmapped = useCallback(async () => {
    try {
      const response = await authFetch('/api/txn-detail-methods/unmapped');
      const data = await response.json();
      setUnmapped(data);
    } catch (error) {
      console.error('Gagal mengambil unmapped methods:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchMethods();
    fetchUnmapped();
  }, [fetchMethods, fetchUnmapped]);

  const handleAddMethod = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch('/api/txn-detail-methods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newMethod),
      });
      if (response.ok) {
        toast.success('Method berhasil ditambahkan');
        setNewMethod({ type: 'Transaction', code: '', name: '', provider: '' });
        setShowAddForm(false);
        fetchMethods();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Gagal menambahkan method');
      }
    } catch (error) {
      toast.error('Gagal menambahkan method');
    }
  };

  const handleRemap = async () => {
    try {
      const response = await authFetch('/api/txn-detail-methods/remap', { method: 'POST' });
      const data = await response.json();
      toast.success(data.message);
      fetchUnmapped();
    } catch (error) {
      toast.error('Gagal remap');
    }
  };

  const handleDeleteMethod = async (id) => {
    if (!window.confirm('Hapus method ini beserta semua aliasnya?')) return;
    try {
      const response = await authFetch(`/api/txn-detail-methods/${id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Method berhasil dihapus');
        fetchMethods();
      } else {
        toast.error('Gagal menghapus method');
      }
    } catch (error) {
      toast.error('Gagal menghapus method');
    }
  };

  const handleAddAlias = async (methodId) => {
    if (!newAlias.trim()) return;
    try {
      const response = await authFetch(`/api/txn-detail-methods/${methodId}/aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias: newAlias.trim() }),
      });
      if (response.ok) {
        toast.success('Alias berhasil ditambahkan');
        setNewAlias('');
        fetchMethods();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Gagal menambahkan alias');
      }
    } catch (error) {
      toast.error('Gagal menambahkan alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      const response = await authFetch(`/api/txn-detail-methods/aliases/${aliasId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Alias berhasil dihapus');
        fetchMethods();
      } else {
        toast.error('Gagal menghapus alias');
      }
    } catch (error) {
      toast.error('Gagal menghapus alias');
    }
  };

  const filteredMethods = methods.filter(m => {
    // Apply filter type
    if (filterType !== 'all' && m.type !== filterType) return false;

    // Apply search term
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      m.type.toLowerCase().includes(term) ||
      m.code.toLowerCase().includes(term) ||
      m.name.toLowerCase().includes(term) ||
      (m.provider && m.provider.toLowerCase().includes(term)) ||
      m.aliases.some(a => a.toLowerCase().includes(term))
    );
  });

  return (
    <div>
      <div className="tab-header-actions">
        <h2>Txn Detail Method & Alias Management</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowUnmapped(!showUnmapped)} className="add-new-btn" style={{ background: unmapped.length > 0 ? '#dc3545' : '#28a745' }}>
            Unmapped ({unmapped.length})
          </button>
          <button onClick={() => setShowAddForm(!showAddForm)} className="add-new-btn">
            {showAddForm ? 'Cancel' : 'Add Method'}
          </button>
        </div>
      </div>

      {showUnmapped && unmapped.length > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <strong>Unmapped Channels (belum ter-mapping ke master data)</strong>
            <button onClick={handleRemap} className="add-new-btn" style={{ padding: '4px 12px', fontSize: '12px' }}>Re-map Now</button>
          </div>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Tambahkan alias yang sesuai di method terkait, lalu klik "Re-map Now".</p>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Raw Channel</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Tipe</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Total Count</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Records</th>
              </tr>
            </thead>
            <tbody>
              {unmapped.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{item.channel}</td>
                  <td style={{ padding: '4px 8px' }}>
                    <span className={`status-badge status-${item.transaction_type === 'Transaction' ? 'success' : 'running'}`} style={{ fontSize: '11px', padding: '2px 6px' }}>
                      {item.transaction_type}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{item.total_count.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{item.record_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUnmapped && unmapped.length === 0 && (
        <div style={{ background: '#d4edda', border: '1px solid #28a745', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
          Semua data channel sudah ter-mapping!
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddMethod} style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', background: '#f8f9fa', padding: '12px', borderRadius: '6px', border: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600' }}>Tipe:</label>
            <select
              value={newMethod.type}
              onChange={e => setNewMethod({ ...newMethod, type: e.target.value })}
              style={{ padding: '6px 10px', height: '34px' }}
            >
              <option value="Transaction">Transaction</option>
              <option value="Disbursement">Disbursement</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600' }}>Kode (e.g. gopay):</label>
            <input
              type="text"
              placeholder="Kode"
              value={newMethod.code}
              onChange={e => setNewMethod({ ...newMethod, code: e.target.value })}
              required
              style={{ padding: '6px 10px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600' }}>Nama Produk (e.g. GoPay):</label>
            <input
              type="text"
              placeholder="Nama"
              value={newMethod.name}
              onChange={e => setNewMethod({ ...newMethod, name: e.target.value })}
              required
              style={{ padding: '6px 10px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600' }}>Penyelenggara (e.g. GoTo Gojek Tokopedia):</label>
            <input
              type="text"
              placeholder="Penyelenggara"
              value={newMethod.provider}
              onChange={e => setNewMethod({ ...newMethod, provider: e.target.value })}
              style={{ padding: '6px 10px', minWidth: '250px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" className="add-new-btn" style={{ height: '34px' }}>Save</button>
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search methods (Kode, nama, penyelenggara, alias)..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ padding: '8px 12px', width: '100%', maxWidth: '350px', margin: 0 }}
        />
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: '600' }}>Filter Tipe:</span>
          <button onClick={() => setFilterType('all')} className={`action-btn ${filterType === 'all' ? 'btn-warning' : ''}`} style={{ padding: '4px 12px', textDecoration: 'none', background: filterType === 'all' ? '#e0a800' : '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Semua</button>
          <button onClick={() => setFilterType('Transaction')} className={`action-btn ${filterType === 'Transaction' ? 'btn-warning' : ''}`} style={{ padding: '4px 12px', textDecoration: 'none', background: filterType === 'Transaction' ? '#e0a800' : '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Transaction</button>
          <button onClick={() => setFilterType('Disbursement')} className={`action-btn ${filterType === 'Disbursement' ? 'btn-warning' : ''}`} style={{ padding: '4px 12px', textDecoration: 'none', background: filterType === 'Disbursement' ? '#e0a800' : '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Disbursement</button>
        </div>
      </div>

      {loading ? (
        <p>Loading data...</p>
      ) : (
        <div className="table-responsive">
          <table className="master-data-table">
            <thead>
              <tr>
                <th>Tipe</th>
                <th>Kode</th>
                <th>Nama Produk</th>
                <th>Nama Penyelenggara</th>
                <th>Aliases</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMethods.map(m => (
                <React.Fragment key={m.id}>
                  <tr>
                    <td>
                      <span className={`status-badge status-${m.type === 'Transaction' ? 'success' : 'running'}`} style={{ fontSize: '12px' }}>
                        {m.type}
                      </span>
                    </td>
                    <td><code>{m.code}</code></td>
                    <td>{m.name}</td>
                    <td>{m.provider || '-'}</td>
                    <td>
                      <span
                        style={{ cursor: 'pointer', color: '#007bff' }}
                        onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
                      >
                        {m.aliases.length} alias {expandedId === m.id ? '▲' : '▼'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button onClick={() => handleDeleteMethod(m.id)} className="delete-btn">Delete</button>
                    </td>
                  </tr>
                  {expandedId === m.id && (
                    <tr>
                      <td colSpan="6" style={{ backgroundColor: '#f8f9fa', padding: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          {m.aliases.map((alias, idx) => (
                            <span key={idx} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              background: '#e9ecef', padding: '4px 8px', borderRadius: '4px', fontSize: '13px'
                            }}>
                              {alias}
                              <button
                                onClick={() => {
                                  authFetch(`/api/txn-detail-methods/${m.id}/aliases`).then(r => r.json()).then(aliases => {
                                    const found = aliases.find(a => a.alias === alias);
                                    if (found) handleDeleteAlias(found.id);
                                  });
                                }}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}
                              >×</button>
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            placeholder="Tambah alias baru..."
                            value={newAlias}
                            onChange={e => setNewAlias(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddAlias(m.id); }}}
                            style={{ padding: '4px 8px', flex: 1, maxWidth: '300px' }}
                          />
                          <button onClick={() => handleAddAlias(m.id)} className="add-new-btn" style={{ padding: '4px 12px' }}>+</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {filteredMethods.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No methods found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TxnDetailMethodTab;
